import React, { useState } from "react";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Breadcrumbs,
  Link as MuiLink,
  Grid,
  Paper,
} from "@mui/material";
import { useDroughtStageUpdate } from "./useDroughtStageUpdate";
import CurrentDroughtAlert from "../../components/droughtDeclaration/CurrentDroughtAlert";
import DroughtDeclarationHistory from "./DroughtDeclarationHistory";
import ZoneCalendarView from "./ZoneCalendarView";
import { useApp } from "../../AppProvider";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import { ROUTES } from "../../constants";

export default function DroughtStageUpdate() {
  const { doToast } = useApp();
  const { stagesQuery, zonesQuery, declarationMutation } =
    useDroughtStageUpdate();

  const [selectedStageByZone, setSelectedStageByZone] = useState({});
  const [changedZones, setChangedZones] = useState({});
  const [dialogData, setDialogData] = useState({
    open: false,
    zoneId: null,
    zoneStageId: null,
  });

  const {
    data: stages,
    isLoading: stagesLoading,
    isError: stagesError,
  } = stagesQuery;
  const {
    data: zones,
    isLoading: zonesLoading,
    isError: zonesError,
  } = zonesQuery;

  if (stagesLoading || zonesLoading) return <CircularProgress />;
  if (stagesError || zonesError || !stages || !zones) {
    return (
      <Alert severity="error">Failed to load drought stages or zones</Alert>
    );
  }

  const zoneMap = zones.reduce((acc, z) => {
    acc[z.zone_ndx] = z.zone_desc;
    return acc;
  }, {});

  const optionsByZone = stages.reduce((acc, s) => {
    const key = s.zone_ndx;
    if (!acc[key]) acc[key] = [];
    acc[key].push({
      id: s.zone_stage_ndx,
      label: `${String(s.stage_label)} | ${
        Number(s.curtailment_pct) || 0
      }% Curtailment`,
      raw: s,
    });
    return acc;
  }, {});

  const handleStageChange = (zoneId, opt) => {
    setSelectedStageByZone((prev) => ({
      ...prev,
      [zoneId]: opt ? opt.id : null,
    }));
    setChangedZones((prev) => ({ ...prev, [zoneId]: !!opt }));
  };

  const openConfirmation = (zoneId) => {
    const zoneStageId = selectedStageByZone[zoneId];
    if (!zoneStageId) return;
    setDialogData({ open: true, zoneId, zoneStageId });
  };

  const closeDialog = () =>
    setDialogData({ open: false, zoneId: null, zoneStageId: null });

  const handleConfirm = () => {
    const { zoneId, zoneStageId } = dialogData;
    declarationMutation.mutate(
      { zoneId, zoneStageId },
      {
        onSuccess: () => {
          setChangedZones((prev) => ({ ...prev, [zoneId]: false }));
          doToast(
            "success",
            `Drought stage updated successfully for ${zoneMap[zoneId]}`
          );
        },
        onError: () => {
          doToast("error", `Failed to update stage for ${zoneMap[zoneId]}`);
        },
      }
    );
    closeDialog();
  };

  return (
    <Box>
      <Helmet title="Drought Stage Management" />

      <Typography variant="h3" gutterBottom display="inline">
        Drought Stage Management
      </Typography>

      <Breadcrumbs sx={{ mt: 4 }} aria-label="breadcrumb">
        <MuiLink component={RouterLink} to={ROUTES.LANDING}>
          Home
        </MuiLink>
        <Typography>Drought Stage Management</Typography>
      </Breadcrumbs>

      <Divider sx={{ my: 6 }} />

      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Current Status & Updates
      </Typography>

      <Grid container spacing={4}>
        {/* Current Drought Stages Column */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: "100%" }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
              Current Drought Stages
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Active drought declarations by zone with curtailment requirements.
            </Typography>
            <CurrentDroughtAlert />
          </Paper>
        </Grid>

        {/* Update Drought Stage Column */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: "100%" }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
              Update Drought Stages
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select a new stage to create a declaration effective immediately.
            </Typography>

            {zones.map((zone) => {
              const zId = zone.zone_ndx;
              const opts = optionsByZone[zId] || [];
              const value =
                opts.find((o) => o.id === selectedStageByZone[zId]) || null;

              return (
                <Box key={zId} sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 500 }}
                  >
                    {zone.zone_desc}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Autocomplete
                      value={value}
                      onChange={(_, newVal) => handleStageChange(zId, newVal)}
                      options={opts}
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      sx={{ flex: 1 }}
                      size="small"
                      renderInput={(params) => (
                        <TextField {...params} label="Select Stage" />
                      )}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={
                        !changedZones[zId] || declarationMutation.isLoading
                      }
                      onClick={() => openConfirmation(zId)}
                    >
                      Submit
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 6 }} />

      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Reporting & Analysis
      </Typography>

      <Box sx={{ mb: 4 }}>
        <DroughtDeclarationHistory />
      </Box>

      <Box sx={{ mb: 4 }}>
        <ZoneCalendarView />
      </Box>

      <Dialog open={dialogData.open} onClose={closeDialog}>
        <DialogTitle>Confirm Stage Change</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to update{" "}
            <strong>{zoneMap[dialogData.zoneId]}</strong> to{" "}
            <strong>
              {
                (optionsByZone[dialogData.zoneId] || []).find(
                  (o) => o.id === dialogData.zoneStageId
                )?.label
              }
            </strong>
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="error" autoFocus>
            Cancel
          </Button>
          <Button onClick={handleConfirm} color="secondary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
